import React, { SetStateAction, useMemo } from "react";
import Text from "@/components/atoms/Text";
import { SettingsFormContent } from "@/components/pages/Settings";
import CTA from "@/components/atoms/CTA";
import { useTranslation } from "react-i18next";
import Space from "@/components/atoms/Space";
import { CompanySpecificService } from "@/types";
import { ColumnDef } from "@tanstack/react-table";
import Group from "@/components/atoms/Group";
import SettingsTable from "@/components/molecules/SettingsTable/SettingsTable";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { disableCompanySpecificService } from "@/services/Company";
import NumberFormatter from "@/components/atoms/NumberFormatter";
import { ErrorNotificationObj } from "@/utils/NotificationUtil";
import { notifications } from "@mantine/notifications";

interface ServiceTableProps {
  services: CompanySpecificService[];
  currentCompanyId: number | undefined;
  isAdmin: boolean;
  setSelectedService: React.Dispatch<SetStateAction<CompanySpecificService | null>>;
  includeDisabled: boolean;
  setIncludeDisabled: React.Dispatch<SetStateAction<boolean>>;
}

const ServiceTable = ({
  services,
  currentCompanyId,
  isAdmin,
  setSelectedService,
  includeDisabled,
  setIncludeDisabled,
}: ServiceTableProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteService } = useMutation({
    mutationFn: async (serviceId: number) => {
      await disableCompanySpecificService(serviceId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanySpecificServices", currentCompanyId],
      });
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.deleteError"), t("notifications:settings.deleteServiceError"))
      );
    },
  });

  const columns: ColumnDef<CompanySpecificService>[] = useMemo(
    () => [
      {
        header: t("settings:serviceSettings.service"),
        accessorKey: "name",
      },
      {
        header: t("settings:serviceSettings.price"),
        accessorKey: "price",
        cell: (info) => <NumberFormatter value={info.row.original.price} suffix={` ${info.row.original.currency}`} />,
      },
      {
        header: t("settings:serviceSettings.paymentFrequency"),
        accessorKey: "servicePriceUnitId",
        cell: (info) => t(`enumerables:paymentIntervals.${info.row.original.priceUnit.code}`),
      },
    ],
    [t]
  );

  const editActions = (service: CompanySpecificService) => [
    {
      label: t("actions:show"),
      action: async () => setSelectedService(service),
    },
    {
      label: t("settings:deactivate"),
      action: async () => deleteService(service.id),
    },
  ];

  return (
    <React.Fragment>
      <SettingsFormContent>
        <Group mb={25} align="center" justify="space-between">
          <Text size="md" strong={true} display="technical" tt="uppercase">
            {t("settings:serviceSettings.serviceList")}
          </Text>
          <CTA intent="secondary" type="button" onClick={() => setIncludeDisabled(!includeDisabled)}>
            {includeDisabled ? t("settings:hideDeactivated") : t("settings:showDeactivated")}
          </CTA>
        </Group>
        <Space height="25px" />
        <SettingsTable data={services} columns={columns} editActions={editActions} isAdmin={isAdmin} />
      </SettingsFormContent>
    </React.Fragment>
  );
};

export default ServiceTable;
